// React entry point.
//
// Bootstrap order matters:
//   1. captureSessionFromUrl() — pull ?rumio_session=<jwt> from the
//      URL the parent iframe loaded with, stash in sessionStorage,
//      strip from URL via history.replaceState.
//   2. startSessionKeepAlive() — renew the 300 s host JWT over postMessage
//      before it expires (see lib/session-refresh.ts).
//   3. attachIframeResizeReporter() — postMessage parent on body
//      height changes so the iframe resizes to fit content.
//   4. Mount React.
//
// Steps 1 and 2 live in packages/web/src/lib/ (added in Phase 6
// scaffold). They're framework-agnostic and run before any React
// component code so the session is available on first render.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { captureSessionFromUrl } from './lib/session';
import { startSessionKeepAlive } from './lib/session-refresh';
import { startIframeResizeReporter } from './lib/iframe-resize';
import { startVersionWatch } from './lib/version-watch';
import { startThemeSync } from './lib/theme';
import { ToastProvider } from './components/ui/Toast';
import { App } from './App';
import './styles.css';

captureSessionFromUrl();
startSessionKeepAlive();
startIframeResizeReporter();
startVersionWatch();
startThemeSync();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Iframe apps reload often as the parent navigates; keep things
      // fresh-ish without thrashing the API on every paint.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('No #root element in document');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
